import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import Link from "@mui/material/Link"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"
import * as React from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"

import Alt from "../components/Alt"
import CupEmblem from "../components/CupEmblem"
import Flag from "../components/Flag"
import Locations from "../components/Locations"
import PlayerName from "../components/PlayerName"
import TableLoading from "../components/TableLoading"
import TimeDetail from "../components/TimeDetail"
import Wheel from "../components/Wheel"
import environment from "../environment"
import { Cup, cupMap } from "../models/cup"
import { Game } from "../models/game"
import { speedMap } from "../models/speed"
import { trackMap } from "../models/track"
import { getLocationCookie, setLocationCookie } from "../utils/cookie"
import { milliToTime } from "../utils/milli-to-time"
import {
  createCustomSearchParams,
  equalSearchParams,
} from "../utils/search-params"

interface WorldRecordRow {
  cup: Cup
  trackId: number
  trackName: string
  speed: number
  time: string
  motion: boolean
  playerId: string
  playerName: string
  playerAlpha3: string
  playerNation: string
  date: string
  isAlt: boolean
  character: number
  variant: number
  miiWeightClass: number
  laps: string[]
  vehicle: number
  tire: number
  glider: number
  coins: string
  shrooms: string
  open: boolean
}

export default function WorldRecords() {
  const { game } = useParams()
  const cups = cupMap[game as Game]
  const speeds = speedMap[game as Game]
  const tracks = trackMap[game as Game]

  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [worldRecordRows, setWorldRecordRows] = useState([] as WorldRecordRow[])
  const [location, setLocation] = useState(
    searchParams.get("location") ?? getLocationCookie()
  )

  useEffect(() => {
    const loc = searchParams.get("location")
    if (loc) {
      setLocationCookie(loc)
      setLocation(loc)
    }
  }, [searchParams])

  useEffect(() => {
    const customSearchParams = createCustomSearchParams({ location: location })
    if (!equalSearchParams(customSearchParams, searchParams)) {
      setSearchParams(customSearchParams)
    }

    fetch(`${environment.domain}/api/${game}/wrs?location=${location}`)
      .then((res) => res.json() as unknown as any)
      .then((res) => {
        const rows = res.rows as any[]
        let worldRecordRows: WorldRecordRow[] = []
        cups.forEach((cup) => {
          tracks
            .filter((track) => track.cup === cup.id)
            .forEach((track) => {
              speeds.forEach((speed) => {
                const val = rows.find(
                  (x) => x.tid === track.id && x.speed === speed
                )
                worldRecordRows.push({
                  cup: cup,
                  trackId: track.id,
                  trackName: track.name,
                  speed: speed,
                  time: val?.time ?? "",
                  motion: val?.motion,
                  playerId: val?.pid ?? "",
                  playerName: val?.name ?? "",
                  playerAlpha3: val?.alpha_3 ?? "",
                  playerNation: val?.country ?? "",
                  date: val?.date?.substring(0, 10) ?? "",
                  isAlt: val?.is_alt,
                  character: val?.character,
                  variant: val?.variant,
                  miiWeightClass: val?.mii_weight_class,
                  laps: val?.laps
                    ? val.laps.map((lap: number) => milliToTime(lap))
                    : [],
                  vehicle: val?.vehicle,
                  tire: val?.tire,
                  glider: val?.glider,
                  coins: val?.coins,
                  shrooms: val?.shrooms,
                  open: false,
                })
              })
            })
        })
        setWorldRecordRows(worldRecordRows)
        setLoading(false)
      })
  }, [location, game, cups, speeds, tracks])

  const navigate = useNavigate()

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", mb: 3, alignItems: "center" }}>
        <Typography variant="h5">World Records</Typography>
        <Box sx={{ flexGrow: 1 }}></Box>
        <Locations
          game={game as Game}
          location={location}
          setLocation={setLocation}
        ></Locations>
      </Box>
      {loading ? (
        <TableLoading />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="wrs">
            <TableHead>
              <TableRow>
                <TableCell>Cup</TableCell>
                <TableCell>Track</TableCell>
                {speeds.length > 1 ? <TableCell>Speed</TableCell> : null}
                <TableCell>Time</TableCell>
                <TableCell>Player</TableCell>
                <TableCell>Nation</TableCell>
                <TableCell>Upload</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {worldRecordRows.map((row, i) => (
                <React.Fragment>
                  <TableRow key={`${row.trackId}${row.speed}`}>
                    {i % (speeds.length * 4) === 0 && (
                      <TableCell rowSpan={speeds.length * 8}>
                        <Box sx={{ textAlign: "center" }}>
                          <CupEmblem cup={row.cup} height="64" />
                        </Box>
                      </TableCell>
                    )}
                    {i % speeds.length === 0 && (
                      <TableCell
                        sx={{ border: speeds.length === 1 ? "none" : null }}
                        rowSpan={speeds.length === 1 ? 1 : speeds.length * 2}
                      >
                        <Link
                          component="a"
                          underline="none"
                          href={`/${game}/trs/${
                            row.trackId
                          }?location=${getLocationCookie()}&speed=${row.speed}`}
                          onClick={(e) => {
                            navigate(
                              `/${game}/trs/${
                                row.trackId
                              }?location=${getLocationCookie()}&speed=${
                                row.speed
                              }`
                            )
                            e.preventDefault()
                          }}
                        >
                          {row.trackName}
                        </Link>
                      </TableCell>
                    )}
                    {speeds.length > 1 ? (
                      <TableCell sx={{ border: "none" }}>
                        <Link
                          component="a"
                          underline="none"
                          href={`/${game}/trs/${
                            row.trackId
                          }?location=${getLocationCookie()}&speed=${row.speed}`}
                          onClick={(e) => {
                            navigate(
                              `/${game}/trs/${
                                row.trackId
                              }?location=${getLocationCookie()}&speed=${
                                row.speed
                              }`
                            )
                            e.preventDefault()
                          }}
                        >
                          {row.speed}cc
                        </Link>
                      </TableCell>
                    ) : null}
                    <TableCell sx={{ border: "none" }}>
                      {row.time}
                      {row.motion ? <Wheel /> : ""}
                    </TableCell>
                    <TableCell sx={{ border: "none" }}>
                      {row.playerId ? (
                        <Link
                          component="a"
                          underline="none"
                          href={`/${game}/players/${
                            row.playerId
                          }?location=${getLocationCookie()}`}
                          onClick={(e) => {
                            navigate(
                              `/${game}/players/${
                                row.playerId
                              }?location=${getLocationCookie()}`
                            )
                            e.preventDefault()
                          }}
                        >
                          <PlayerName name={row.playerName} />
                        </Link>
                      ) : null}
                      {row.isAlt ? <Alt /> : null}
                    </TableCell>
                    <TableCell sx={{ border: "none" }}>
                      <Flag
                        alpha3={row.playerAlpha3}
                        country={row.playerNation}
                        tooltip={true}
                      />
                    </TableCell>
                    <TableCell sx={{ border: "none" }}>{row.date}</TableCell>
                    <TableCell
                      align="right"
                      sx={{ paddingTop: 0, paddingBottom: 0, border: "none" }}
                    >
                      {row.date ? (
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          sx={{ height: "26px", width: "26px" }}
                          onClick={() => {
                            const newWorldRecordRows = [...worldRecordRows]
                            newWorldRecordRows[i].open = !row.open
                            setWorldRecordRows(newWorldRecordRows)
                          }}
                        >
                          {row.open ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      ) : null}
                    </TableCell>
                  </TableRow>
                  <TimeDetail
                    open={row.open}
                    trackId={row.trackId}
                    playerId={row.playerId}
                    game={game as Game}
                    character={row.character}
                    variant={row.variant}
                    miiWeightClass={row.miiWeightClass}
                    laps={row.laps}
                    vehicle={row.vehicle}
                    tire={row.tire}
                    glider={row.glider}
                    coins={row.coins}
                    shrooms={row.shrooms}
                  ></TimeDetail>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}
